import {
  AlertTriangle,
  CalendarClock,
  CircleDot,
  Factory,
  GitBranch,
  GitPullRequest,
  Hand,
  Layers,
  ListFilter,
  Tag,
  Timer,
  User,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { GROUPINGS, type IssueGrouping, issueGrouping } from "../../lib/issue-views";
import { personLabel } from "../../lib/people";
import { type IssuesMeta, WAITING_ON } from "../../types";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "../ui/select";
import { PRIORITY_LABELS, PriorityIcon, SLA_LABELS, StatusDisc, TypeIcon, WAITING_LABELS } from "./meta";

interface FilterDef {
  key: string;
  label: string;
  icon: typeof Tag;
  options: Array<{ value: string; label: string; icon?: React.ReactNode }>;
}

/** Filters that live in the URL, so views are shareable and the back button works. */
export const FILTER_KEYS = [
  "status",
  "statusCategory",
  "type",
  "team",
  "repo",
  "priority",
  "label",
  "reporter",
  "reviewer",
  "involves",
  "slaStatus",
  "waitingOn",
  "hasPr",
  "inFactory",
  "hasParent",
  "hasSubIssues",
] as const;

const GROUP_LABELS: Record<Exclude<IssueGrouping, "none">, string> = {
  status: "Status",
  type: "Type",
  priority: "Priority",
  team: "Team",
  repo: "Repo",
  reviewer: "Reviewer",
  reporter: "Reporter",
  label: "Label",
  sla: "SLA",
  waitingOn: "Waiting on",
};

export function useIssueFilters() {
  const [params, setParams] = useSearchParams();
  const toggle = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    const current = next.get(key)?.split(",").filter(Boolean) ?? [];
    const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    if (updated.length) next.set(key, updated.join(","));
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const set = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const clear = () => {
    const next = new URLSearchParams(params);
    for (const key of [...FILTER_KEYS, "q", "dueBefore"]) next.delete(key);
    setParams(next, { replace: true });
  };
  /** Several keys in one write; separate `set` calls would each start from the same render's params. */
  const replace = (next: URLSearchParams) => setParams(next, { replace: true });
  return { params, toggle, set, clear, replace };
}

export function FilterBar({
  meta,
  children,
}: {
  meta: IssuesMeta | undefined;
  /** Extra controls rendered right after the Filter button, before the active chips. */
  children?: ReactNode;
}) {
  const { params, toggle, set, clear } = useIssueFilters();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<string | null>(null);

  const defs = useMemo<FilterDef[]>(() => {
    if (!meta) return [];
    const statusByKey = new Map(meta.statuses.map((status) => [status.key, status]));
    // `me` resolves to whoever opens the link, so a shared view works for anyone.
    const people = [
      { value: "me", label: "Me" },
      ...meta.users.map((user) => ({ value: user.id, label: personLabel(user) })),
    ];
    return [
      {
        key: "status",
        label: "Status",
        icon: CircleDot,
        options: meta.statuses.map((status) => ({
          value: status.key,
          label: status.name,
          icon: <StatusDisc category={status.category} color={status.color} />,
        })),
      },
      {
        key: "statusCategory",
        label: "Status type",
        icon: Layers,
        options: ["backlog", "unstarted", "started", "completed", "cancelled"].map((cat) => ({
          value: cat,
          label: cat[0]!.toUpperCase() + cat.slice(1),
          icon: <StatusDisc category={cat} color={statusByKey.get(cat)?.color} />,
        })),
      },
      {
        key: "type",
        label: "Type",
        icon: Tag,
        options: meta.types.map((type) => ({
          value: type.key,
          label: type.name,
          icon: <TypeIcon typeKey={type.key} color={type.color} />,
        })),
      },
      {
        key: "team",
        label: "Team",
        icon: Users,
        options: meta.teams.map((team) => ({
          value: team.key,
          label: team.name,
          icon: <span className="size-2.5 rounded-sm" style={{ backgroundColor: team.color }} />,
        })),
      },
      {
        key: "repo",
        label: "Repo",
        icon: GitBranch,
        options: meta.repos.map((repo) => ({
          value: repo.key,
          label: repo.active ? repo.name : `${repo.name} (inactive)`,
          icon: <GitBranch className="size-3.5 text-muted-foreground" />,
        })),
      },
      {
        key: "priority",
        label: "Priority",
        icon: AlertTriangle,
        options: [1, 2, 3, 4, 0].map((priority) => ({
          value: String(priority),
          label: PRIORITY_LABELS[priority]!,
          icon: <PriorityIcon priority={priority} />,
        })),
      },
      {
        key: "label",
        label: "Labels",
        icon: Tag,
        options: meta.labels.map((label) => ({
          value: label.name,
          label: label.name,
          icon: <span className="size-2.5 rounded-full" style={{ backgroundColor: label.color }} />,
        })),
      },
      { key: "reporter", label: "Reporter", icon: User, options: people },
      { key: "reviewer", label: "Reviewer", icon: UserCheck, options: people },
      { key: "involves", label: "Reporter or reviewer", icon: Users, options: people },
      {
        key: "slaStatus",
        label: "SLA",
        icon: Timer,
        options: (["on_track", "at_risk", "breached", "met", "none"] as const).map((status) => ({
          value: status,
          label: SLA_LABELS[status],
        })),
      },
      {
        key: "waitingOn",
        label: "Waiting on",
        icon: Hand,
        options: WAITING_ON.map((reason) => ({ value: reason, label: WAITING_LABELS[reason] })),
      },
      {
        key: "hasPr",
        label: "Pull request",
        icon: GitPullRequest,
        options: [
          { value: "1", label: "Has a pull request" },
          { value: "0", label: "No pull request" },
        ],
      },
      {
        key: "inFactory",
        label: "In factory",
        icon: Factory,
        options: [
          { value: "1", label: "On the factory line" },
          { value: "0", label: "Not in the factory" },
        ],
      },
      {
        key: "hasParent",
        label: "Relations",
        icon: Layers,
        options: [
          { value: "1", label: "Is a sub-issue" },
          { value: "0", label: "Is not a sub-issue" },
        ],
      },
      {
        key: "hasSubIssues",
        label: "Sub-issues",
        icon: Layers,
        options: [
          { value: "1", label: "Has sub-issues" },
          { value: "0", label: "No sub-issues" },
        ],
      },
      {
        key: "dueBefore",
        label: "Due date",
        icon: CalendarClock,
        options: [
          { value: hoursFromNow(8), label: "Due in the next 8 hours" },
          { value: hoursFromNow(24), label: "Due in the next day" },
          { value: hoursFromNow(24 * 7), label: "Due in the next week" },
        ],
      },
    ];
  }, [meta]);

  const active = defs.flatMap((def) => {
    const values = params.get(def.key)?.split(",").filter(Boolean) ?? [];
    return values.map((value) => ({
      def,
      value,
      label: def.options.find((o) => o.value === value)?.label ?? value,
    }));
  });
  const singleValueKeys = new Set(["hasPr", "inFactory", "hasParent", "hasSubIssues", "dueBefore"]);
  const current = defs.find((def) => def.key === category);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setCategory(null);
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 border-dashed">
            <ListFilter className="size-3.5" />
            Filter
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput placeholder={current ? `Filter ${current.label.toLowerCase()}…` : "Filter…"} />
            <CommandList>
              <CommandEmpty>Nothing matches.</CommandEmpty>
              {!current ? (
                <CommandGroup>
                  {defs.map((def) => (
                    <CommandItem key={def.key} value={def.label} onSelect={() => setCategory(def.key)}>
                      <def.icon className="size-4 text-muted-foreground" />
                      {def.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : (
                <CommandGroup heading={current.label}>
                  {current.options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => {
                        if (singleValueKeys.has(current.key)) {
                          set(
                            current.key,
                            params.get(current.key) === option.value ? undefined : option.value,
                          );
                        } else {
                          toggle(current.key, option.value);
                        }
                        setOpen(false);
                        setCategory(null);
                      }}
                    >
                      {option.icon}
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {children}

      {active.map(({ def, value, label }) => (
        <Badge key={`${def.key}:${value}`} variant="secondary" className="gap-1 rounded-full font-normal">
          <span className="text-muted-foreground">{def.label}:</span>
          {label}
          <button
            type="button"
            aria-label={`Remove ${def.label} ${label}`}
            className="ml-0.5 rounded-full hover:text-foreground"
            onClick={() => (singleValueKeys.has(def.key) ? set(def.key, undefined) : toggle(def.key, value))}
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      {active.length > 0 && (
        <Button variant="ghost" size="sm" className="h-7 text-muted-foreground" onClick={clear}>
          Clear
        </Button>
      )}
    </div>
  );
}

const ORDERINGS = [
  ["updated", "Last updated"],
  ["created", "Newest"],
  ["priority", "Priority"],
  ["due", "SLA due"],
  ["number", "Number"],
] as const;

export function GroupSortControls({ defaultGroupBy = "status" }: { defaultGroupBy?: IssueGrouping }) {
  const { params, set } = useIssueFilters();
  return (
    <div className="flex items-center gap-2">
      <Select
        value={issueGrouping(params, defaultGroupBy)}
        onValueChange={(v) => set("groupBy", v === defaultGroupBy ? undefined : v)}
      >
        <SelectTrigger size="sm" className="h-7 w-40" aria-label="Group issues">
          <SelectValue placeholder="Group by" />
        </SelectTrigger>
        <SelectContent>
          {GROUPINGS.filter((key) => key !== "none").map((key) => (
            <SelectItem key={key} value={key}>
              Group: {GROUP_LABELS[key]}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value="none">No grouping</SelectItem>
        </SelectContent>
      </Select>
      <Select value={params.get("orderBy") ?? "created"} onValueChange={(v) => set("orderBy", v)}>
        <SelectTrigger size="sm" className="h-7 w-36" aria-label="Sort issues">
          <SelectValue placeholder="Sort" />
        </SelectTrigger>
        <SelectContent>
          {ORDERINGS.map(([value, label]) => (
            <SelectItem key={value} value={value}>
              Sort: {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function hoursFromNow(hours: number): string {
  return new Date(Date.now() + hours * 3600_000).toISOString();
}
